import React, { useState, useEffect } from 'react';
import { ref, onValue, set } from 'firebase/database';
import { database } from '../firebase';
import './PlanManager.css';

const PLAN_TEMPLATES = {
    basic: {
        name: 'Básico',
        features: ['5 Pacientes', 'Planos Alimentares', 'Calculadora Nutricional'],
        color: '#6C63FF',
        icon: '🌱'
    },
    intermediate: {
        name: 'Intermediário',
        features: ['15 Pacientes', 'Planos Alimentares', 'Calculadora Nutricional', 'Chat com Pacientes', 'Relatórios'],
        color: '#00D09C',
        icon: '🚀'
    },
    advanced: {
        name: 'Avançado',
        features: ['Pacientes Ilimitados', 'Todas as Funcionalidades', 'Suporte Prioritário', 'API Access', 'Whitelabel'],
        color: '#FF6B6B',
        icon: '👑'
    }
};

export default function PlanManager() {
    const [plans, setPlans] = useState({
        basic: { price: '29.90', maxPatients: 5 },
        intermediate: { price: '59.90', maxPatients: 15 },
        advanced: { price: '99.90', maxPatients: -1 }
    });
    
    const [editingPlan, setEditingPlan] = useState(null);
    const [tempPrice, setTempPrice] = useState('');
    const [tempMaxPatients, setTempMaxPatients] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const plansRef = ref(database, 'subscriptionPlans');
        
        const unsubscribe = onValue(plansRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                // Merge com valores padrão para garantir que todos os planos existam
                setPlans({
                    basic: { price: '29.90', maxPatients: 5, ...data.basic },
                    intermediate: { price: '59.90', maxPatients: 15, ...data.intermediate },
                    advanced: { price: '99.90', maxPatients: -1, ...data.advanced }
                });
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const startEditing = (planType) => {
        if (!plans[planType]) return;
        setEditingPlan(planType);
        setTempPrice(plans[planType]?.price || '');
        setTempMaxPatients(plans[planType]?.maxPatients?.toString() || '');
    };

    const savePlan = async (planType) => {
        setSaving(true);
        try {
            const planRef = ref(database, `subscriptionPlans/${planType}`);
            await set(planRef, {
                price: tempPrice,
                maxPatients: parseInt(tempMaxPatients),
                name: PLAN_TEMPLATES[planType].name,
                features: PLAN_TEMPLATES[planType].features,
                updatedAt: new Date().toISOString()
            });
            
            setEditingPlan(null);
            
            // Show success notification
            showNotification('Plano atualizado com sucesso!', 'success');
        } catch (error) {
            console.error('Erro ao salvar plano:', error);
            showNotification('Erro ao salvar plano', 'error');
        } finally {
            setSaving(false);
        }
    };

    const showNotification = (message, type) => {
        // Simple notification implementation
        const notification = document.createElement('div');
        notification.className = `notification notification-${type}`;
        notification.textContent = message;
        document.body.appendChild(notification);
        
        setTimeout(() => {
            notification.classList.add('show');
        }, 10);
        
        setTimeout(() => {
            notification.classList.remove('show');
            setTimeout(() => {
                document.body.removeChild(notification);
            }, 300);
        }, 3000);
    };

    if (loading) {
        return (
            <div className="plan-manager-loading">
                <div className="spinner"></div>
                <p>Carregando planos...</p>
            </div>
        );
    }

    return (
        <div className="plan-manager">
            <div className="plan-manager-header">
                <h2>💎 Gerenciamento de Planos</h2>
                <p>Configure os planos de assinatura disponíveis para os nutricionistas</p>
            </div>

            <div className="plans-grid">
                {Object.entries(PLAN_TEMPLATES).map(([planType, template]) => {
                    // Verificar se o plano existe antes de renderizar
                    if (!plans[planType]) {
                        return null;
                    }
                    
                    return (
                    <div 
                        key={planType} 
                        className={`plan-card ${editingPlan === planType ? 'editing' : ''}`}
                        style={{ '--plan-color': template.color }}
                    >
                        <div className="plan-header">
                            <span className="plan-icon">{template.icon}</span>
                            <h3>{template.name}</h3>
                        </div>

                        <div className="plan-price">
                            {editingPlan === planType ? (
                                <div className="edit-price">
                                    <span className="currency">R$</span>
                                    <input
                                        type="text"
                                        value={tempPrice}
                                        onChange={(e) => setTempPrice(e.target.value)}
                                        placeholder="0.00"
                                        className="price-input"
                                    />
                                    <span className="period">/mês</span>
                                </div>
                            ) : (
                                <>
                                    <span className="currency">R$</span>
                                    <span className="amount">{plans[planType]?.price || '0.00'}</span>
                                    <span className="period">/mês</span>
                                </>
                            )}
                        </div>

                        <div className="plan-limit">
                            {editingPlan === planType ? (
                                <div className="edit-limit">
                                    <label>Limite de Pacientes:</label>
                                    <input
                                        type="number"
                                        value={tempMaxPatients}
                                        onChange={(e) => setTempMaxPatients(e.target.value)}
                                        placeholder="0"
                                        className="limit-input"
                                    />
                                </div>
                            ) : (
                                <div className="limit-display">
                                    <span className="limit-icon">👥</span>
                                    <span className="limit-text">
                                        {plans[planType]?.maxPatients === -1 
                                            ? 'Ilimitado' 
                                            : `Até ${plans[planType]?.maxPatients || 0} pacientes`}
                                    </span>
                                </div>
                            )}
                        </div>

                        <ul className="plan-features">
                            {template.features.map((feature, index) => (
                                <li key={index}>
                                    <span className="check-icon">✓</span>
                                    {feature}
                                </li>
                            ))}
                        </ul>

                        <div className="plan-actions">
                            {editingPlan === planType ? (
                                <>
                                    <button
                                        onClick={() => savePlan(planType)}
                                        className="btn btn-save"
                                        disabled={saving}
                                    >
                                        {saving ? '💾 Salvando...' : '💾 Salvar'}
                                    </button>
                                    <button
                                        onClick={() => setEditingPlan(null)}
                                        className="btn btn-cancel"
                                        disabled={saving}
                                    >
                                        Cancelar
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={() => startEditing(planType)}
                                    className="btn btn-edit"
                                >
                                    ✏️ Editar Plano
                                </button>
                            )}
                        </div>
                    </div>
                    );
                })}
            </div>
        </div>
    );
}
